import type { TFunction } from 'i18next';
import type { AssessmentResultItemDataFragment } from '../../../Trainee/TraineeReports.api';
import type { PsychologicalResult, RadarDatum } from '../../model';
import formatScoreOutOf from '../formatters';
import { teamResilienceHeight } from '.';
import type { TeamResilienceAssessmentResultDataFragment } from '@/pages/AssessmentLauncher/TeamResilience/TeamResilience.api';
import { DefaultTeamResilienceQuestionCategory } from '@/pages/AssessmentManagementPage/shared/ConfigurationSchema/teamResilienceFormSchema';

const parseTeamResilience = (
    t: TFunction,
    items: AssessmentResultItemDataFragment[],
    teamResilienceItems?: AssessmentResultItemDataFragment[] | null
): PsychologicalResult | null => {
    const item = items.find(i => i.result.__typename === 'TeamResilienceResult');
    const moduleInstance = item?.moduleInstance;
    const teamResilienceResult = item?.result as TeamResilienceAssessmentResultDataFragment;

    if (!moduleInstance?.moduleDefinitionIdentifier) {
        return null;
    }

    if (!teamResilienceResult?.subcategoryAverages?.length) {
        return null;
    }

    const individualResources =
        teamResilienceResult?.subcategoryAverages.filter(
            s => s.category === DefaultTeamResilienceQuestionCategory.TeamResources
        ) || [];

    if (individualResources.length === 0) {
        return null;
    }

    const individualRadarData: RadarDatum[] = individualResources.map(s => ({
        axis: s.subcategory,
        individual: Number(s.averageScore.toFixed(2)),
    }));
    const maxDomain = Math.max(1, ...individualResources.map(s => s.maximumPossibleScore ?? 0));

    const scoreCards = individualResources.map(subcategory => ({
        label: subcategory.subcategory,
        value: formatScoreOutOf(t, subcategory.averageScore, subcategory.maximumPossibleScore, 1),
    }));

    const teamAgg = (teamResilienceItems || [])
        .filter(i => i.result.__typename === 'TeamResilienceResult')
        .reduce<Record<string, { total: number; count: number }>>((acc, i) => {
            if (i.result.__typename !== 'TeamResilienceResult') {
                return acc;
            }
            const tr = i.result as TeamResilienceAssessmentResultDataFragment;
            tr.subcategoryAverages
                .filter(sa => sa.category === DefaultTeamResilienceQuestionCategory.TeamResources)
                .forEach(sa => {
                    const key = sa.subcategory;
                    if (!acc[key]) {
                        acc[key] = { total: 0, count: 0 };
                    }
                    acc[key].total += sa.averageScore;
                    acc[key].count += 1;
                });
            return acc;
        }, {});

    const teamRadarData: RadarDatum[] = individualResources.map(s => {
        const agg = teamAgg[s.subcategory];
        const rawAvg = agg && agg.count > 0 ? agg.total / agg.count : null;
        const avg = rawAvg != null && Number.isFinite(rawAvg) ? Number(rawAvg.toFixed(2)) : 0;
        return {
            axis: s.subcategory,
            individual: Number(s.averageScore.toFixed(2)),
            team: avg,
        };
    });

    return {
        moduleDefinitionIdentifier: moduleInstance.moduleDefinitionIdentifier,
        name: moduleInstance.name ?? '',
        description: moduleInstance.description ?? '',
        radarGroups: [
            {
                key: 'team-resilience',
                scoreCards,
                charts: [
                    {
                        data: individualRadarData,
                        maxDomain,
                        height: teamResilienceHeight,
                        variant: 'bare',
                        footer: t('traineeReports:labels.individual'),
                        showTeam: false,
                    },
                    {
                        data: teamRadarData,
                        maxDomain,
                        height: teamResilienceHeight,
                        variant: 'bare',
                        footer: t('traineeReports:labels.team'),
                        showTeam: true,
                    },
                ],
            },
        ],
    };
};

export default parseTeamResilience;
